

import { useEffect, useMemo, useRef, useState } from 'react';
import './Products.css';

const PRODUCT_CATEGORIES = [
  { slug: 'aisan', label: 'Aisan', description: 'Precision carburetor options for imported and specialty applications.' },
  { slug: 'carburetor_electric_choke', label: 'Carburetor Electric Choke', description: 'Electric choke carburetors built for reliable starts and easier daily drivability.' },
  { slug: 'carter_afb_marine', label: 'Carter AFB Marine', description: 'Marine-ready Carter AFB units for dependable fuel delivery on the water.' },
  { slug: 'carter_afb_marine_carburetor', label: 'Carter AFB Marine Carburetor', description: 'Detailed Carter AFB marine carburetor inventory and fitment-focused presentation.' },
  { slug: 'carter_bbd_carburetor', label: 'Carter BBD Carburetor', description: 'Classic Carter BBD carburetors for restoration, replacement, and tuning work.' },
  { slug: 'carter_bbd_carburetor_jeep', label: 'Carter BBD Carburetor Jeep', description: 'Jeep-focused Carter BBD options for proven utility and rugged drivability.' },
  { slug: 'carter_marine_carburetor', label: 'Carter Marine Carburetor', description: 'Marine Carter carburetor selections built for performance and durability.' },
  { slug: 'carter_weber_marine_carburetors', label: 'Carter Weber Marine Carburetors', description: 'Carter Weber marine carburetors for niche applications and specialty builds.' },
  { slug: 'holley_four_barrel_4160', label: 'Holley Four Barrel 4160', description: 'Performance-oriented Holley 4160 selections with classic four-barrel appeal.' },
  { slug: 'holley_marine_carburetor_two_barrel', label: 'Holley Marine Carburetor Two Barrel', description: 'Two-barrel Holley marine carbs suited for dependable boating applications.' },
  { slug: 'hotrod', label: 'Hot Rod', description: 'Hot rod carburetor category for custom builds, attitude, and throttle response.' },
  { slug: 'jeep_carburetor', label: 'Jeep Carburetor', description: 'Jeep carburetor category focused on off-road reliability and practical fitment.' },
  { slug: 'marine', label: 'Marine', description: 'Marine carburetor products designed for corrosion resistance and consistent delivery.' },
  { slug: 'penta_marine', label: 'Penta Marine', description: 'Volvo Penta and related marine carburetor offerings for marine performance.' },
  { slug: 'quadrajet_volvo', label: 'Quadrajet Volvo', description: 'Quadrajet configurations tailored to Volvo applications and marine crossover needs.' },
  { slug: 'ratrod', label: 'Rat Rod', description: 'Rat rod carburetor options with personality for unique builds and experimental setups.' },
  { slug: 'rochester_b_model_hand_choke_carburetor', label: 'Rochester B Model Hand Choke Carburetor', description: 'Rochester B model hand choke options for vintage drivetrains and restoration work.' },
  { slug: 'rochester_four_4_barrel_carburetor_e4me', label: 'Rochester Four 4 Barrel Carburetor E4ME', description: 'Rochester E4ME four-barrel carburetors for classic power and drivability.' },
  { slug: 'rochester_four_barrel_careburetor_marine_omc', label: 'Rochester Four Barrel Carburetor Marine OMC', description: 'Four-barrel Rochester marine carburetors for OMC-related setups.' },
  { slug: 'rochester_marine_carburetors_twin_set_up', label: 'Rochester Marine Carburetors Twin Set Up', description: 'Twin setup Rochester marine configurations for specialized marine performance.' },
  { slug: 'rochester_marine_quadrajet', label: 'Rochester Marine Quadrajet', description: 'Marine Quadrajet options for broad compatibility and proven Rochester heritage.' },
  { slug: 'rochester_marine_quadrajet_carburetor', label: 'Rochester Marine Quadrajet Carburetor', description: 'Detailed Rochester marine Quadrajet category with fitment-first presentation.' },
  { slug: 'rochester_marine_quadrajet_carburetor_volvo_penta', label: 'Rochester Marine Quadrajet Carburetor Volvo Penta', description: 'Volvo Penta-focused Rochester marine Quadrajet solutions.' },
  { slug: 'rochester_marine_two_barrel_carburetor_elec_choke_at_the_base', label: 'Rochester Marine Two Barrel Carburetor Electric Choke At The Base', description: 'Two-barrel Rochester marine options with electric choke at the base.' },
  { slug: 'rochester_performance_quadrajet_carburetor', label: 'Rochester Performance Quadrajet Carburetor', description: 'Performance Rochester Quadrajet offerings for strong response and classic tuning.' },
  { slug: 'rochester_quadrajet_marine_carburetors', label: 'Rochester Quadrajet Marine Carburetors', description: 'Marine Quadrajet inventory for marine rebuild, replacement, and performance work.' },
  { slug: 'rochester_two_barrel', label: 'Rochester Two Barrel', description: 'Two-barrel Rochester carburetors for dependable classic applications.' },
  { slug: 'rochester_two_barrel_marine_carburetor', label: 'Rochester Two Barrel Marine Carburetor', description: 'Marine-ready Rochester two-barrel carburetors for reliable water use.' },
  { slug: 'rochester_verajet_jeep_carburetor_2.8', label: 'Rochester Verajet Jeep Carburetor 2.8', description: 'Rochester Verajet 2.8 Jeep options focused on niche fitment and restoration needs.' },
  { slug: 'stromberg97', label: 'Stromberg 97', description: 'Legendary Stromberg 97 carburetor presentation for vintage and performance fans.' },
  { slug: 'stromberg', label: 'Stromberg', description: 'Stromberg carburetor category for classic builds and specialty applications.' },
  { slug: 'toyota', label: 'Toyota', description: 'Toyota carburetor selections for classic models and practical replacement needs.' },
  { slug: 'two_barrel_rochester_marine_2gc_rebuild_kit', label: 'Two Barrel Rochester Marine 2GC Rebuild Kit', description: 'Rochester 2GC rebuild kit category for marine maintenance and restoration work.' },
];

function buildCategoryMedia(slug) {
  return {
    hero: `/assets/images/${slug}/main.jpg`,
    gallery: [1, 2, 3, 4, 5].map((number) => `/assets/images/${slug}/gallery-${number}.jpg`),
    video: `/assets/videos/${slug}/preview.mp4`,
  };
}

function MediaImage({ src, alt, className }) {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className={`${className} products-media-placeholder`}>
        <span>Image placeholder</span>
      </div>
    );
  }

  return <img className={className} src={src} alt={alt} onError={() => setHasError(true)} />;
}

function MediaVideo({ src, title }) {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className="products-video-placeholder">
        <span>Video placeholder</span>
      </div>
    );
  }

  return (
    <video className="products-video" controls preload="metadata" onError={() => setHasError(true)}>
      <source src={src} type="video/mp4" />
      {title}
    </video>
  );
}

function ProductCard({ category, media, sectionRef, isFocused = false }) {
  return (
    <section
      id={category.slug}
      ref={sectionRef}
      className={`products-section ${isFocused ? 'products-section--focused' : ''}`}
    >
      <div className="products-section__header">
        <div>
          <span className="products-section__eyebrow">Category</span>
          <h2 className="products-section__title">{category.label}</h2>
          <p className="products-section__description">{category.description}</p>
        </div>
        <a className="products-section__anchor" href={`#${category.slug}`}>
          #{category.slug}
        </a>
      </div>

      <div className="products-hero-card">
        <div className="products-hero-card__media-wrap">
          <MediaImage src={media.hero} alt={`${category.label} main product view`} className="products-hero-card__image" />
        </div>
        <div className="products-hero-card__content">
          <span className="products-pill">Main image</span>
          <h3>{category.label} Collection</h3>
          <p>
            This category is ready for your strongest featured image, supporting gallery, and demo video. It is built to
            work directly with the sidebar category links and future Odoo-connected product listings.
          </p>
          <div className="products-feature-grid">
            <div className="products-feature-box">
              <strong>1 Main Image</strong>
              <span>Primary showcase visual</span>
            </div>
            <div className="products-feature-box">
              <strong>5 Gallery Images</strong>
              <span>Supporting photo set</span>
            </div>
            <div className="products-feature-box">
              <strong>1 Video</strong>
              <span>Preview or walkthrough</span>
            </div>
          </div>
        </div>
      </div>

      <div className="products-gallery-grid">
        {media.gallery.map((imageSrc, index) => (
          <article key={imageSrc} className="products-gallery-card">
            <MediaImage
              src={imageSrc}
              alt={`${category.label} gallery image ${index + 1}`}
              className="products-gallery-card__image"
            />
            <div className="products-gallery-card__body">
              <span className="products-pill products-pill--muted">Gallery {index + 1}</span>
              <h4>{category.label}</h4>
              <p>Use this image slot for detail views, installed examples, angle shots, or product packaging.</p>
            </div>
          </article>
        ))}
      </div>

      <div className="products-video-card">
        <div className="products-video-card__copy">
          <span className="products-pill">Video slot</span>
          <h3>{category.label} Video Showcase</h3>
          <p>Add a short install clip, startup demo, testing footage, or a quick explanation video for this category.</p>
        </div>
        <MediaVideo src={media.video} title={`${category.label} preview video`} />
      </div>
    </section>
  );
}

function Products() {
  const [activeHash, setActiveHash] = useState(window.location.hash.replace('#', '') || '');
  const sectionRefs = useRef({});

  useEffect(() => {
    const syncHash = () => {
      setActiveHash(window.location.hash.replace('#', '') || '');
    };

    syncHash();
    window.addEventListener('hashchange', syncHash);

    return () => {
      window.removeEventListener('hashchange', syncHash);
    };
  }, []);

  useEffect(() => {
    if (!activeHash) return;

    const target = sectionRefs.current[activeHash] || document.getElementById(activeHash);
    if (target) {
      window.setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    }
  }, [activeHash]);

  const previewCategories = useMemo(() => PRODUCT_CATEGORIES.slice(0, 8), []);

  return (
    <div className="products-page">
      <section className="products-page__hero">
        <div className="products-page__hero-copy">
          <span className="products-page__eyebrow">CarbCareUSA Product Categories</span>
          <h1>Browse every carburetor family with a premium product showcase layout.</h1>
          <p>
            This page is built to work with your sidebar categories. Each section is hash-linked, media-ready, and prepared
            for a main image, five supporting images, and one video per product family.
          </p>
        </div>
        <div className="products-page__hero-stats">
          <div className="products-stat-card">
            <strong>{PRODUCT_CATEGORIES.length}</strong>
            <span>Product categories</span>
          </div>
          <div className="products-stat-card">
            <strong>7 media slots</strong>
            <span>Per category section</span>
          </div>
          <div className="products-stat-card">
            <strong>Sidebar linked</strong>
            <span>Ready for navigation</span>
          </div>
        </div>
      </section>

      <section className="products-page__jumpbar">
        {previewCategories.map((category) => (
          <a key={category.slug} href={`#${category.slug}`} className="products-jump-chip">
            {category.label}
          </a>
        ))}
      </section>

      <section className="products-page__sections">
        {PRODUCT_CATEGORIES.map((category) => {
          const media = buildCategoryMedia(category.slug);

          return (
            <div
              key={category.slug}
              className={
                activeHash === category.slug
                  ? 'products-section-wrap products-section-wrap--focused'
                  : 'products-section-wrap'
              }
            >
              <ProductCard
                category={category}
                media={media}
                isFocused={activeHash === category.slug}
                sectionRef={(element) => {
                  if (element) {
                    sectionRefs.current[category.slug] = element;
                  }
                }}
              />
            </div>
          );
        })}
      </section>
    </div>
  );
}

export default Products;